import { useCallback, useMemo } from 'react';
import { Col } from '@amzn/stencil-react-components/layout';

import { GroupedOptionMultiSelect } from '../../../shared/ui/forms/GroupedOptionMultiSelect';
import {
  tagsToOptionGroups,
  tagMapToSelectedValues,
  selectedValuesToTagMap,
  subtractOptionGroups,
  type TagOptionGroup,
} from '../../../shared/ui/forms/tagOptionCodec';
import { ConfigField } from './ConfigField';
import { useMessages } from '../../../shared/i18n/useMessages';
import { useCurrentUser } from '../../../shared/auth';
import { useTagsQuery } from '../../../shared/data/tags/useTagsQuery';
import {
  CONFIG_BUNDLE,
  type ConfigKey,
  dtProps,
  NO_CONFIG_FIELD_ERRORS,
  type QuestionConfigCardProps,
} from './questionConfigShared';
import type { TagMap } from 'src/types/helios/Question';

/** Reference-stable empty TagMap so an absent `tagsExcluded` doesn't rebuild
 *  the excluded-selection memo each render. */
const EMPTY_TAG_MAP: TagMap = {};

/**
 * The Tags Included + Tags Excluded config fields. Both draw from the same
 * BU + program-scoped tag catalog, cross-fed so a (group, value) leaf chosen
 * in one is removed from the other's options (mirrors the manual flow). Tags
 * Excluded is offered only once at least one tag is Included.
 *
 * Extracted from {@link QuestionConfigBasicInformation} so each component stays
 * within the file-size limit; the two selects are a cohesive unit (shared
 * catalog + cross-feed) that belongs together.
 */
export function TagsConfigFields({
  config,
  patchConfig,
  fieldErrors = NO_CONFIG_FIELD_ERRORS,
  disabled,
  dataTestId,
}: QuestionConfigCardProps): JSX.Element {
  const { t } = useMessages<ConfigKey>(CONFIG_BUNDLE);
  const infoAltText = t('configInfoAltText');

  const { businessUnit, programId } = useCurrentUser();
  const businessUnitId = businessUnit ?? '';
  const { data: tagsData } = useTagsQuery(
    { businessUnitId, programId, isActive: true },
    { enabled: !!businessUnitId && !!programId },
  );

  // Grouped option universe from the tag catalog (active tags/values,
  // alphabetised); the draft's stored TagMaps project onto the flat encoded
  // leaf lists the grouped select consumes. Memoised for the
  // reference-stable-options contract.
  const tagOptionGroups = useMemo<readonly TagOptionGroup[]>(
    () => tagsToOptionGroups(tagsData?.tags ?? []),
    [tagsData],
  );
  const selectedTagValues = useMemo<readonly string[]>(
    () => tagMapToSelectedValues(config.tagsIncluded),
    [config.tagsIncluded],
  );
  const selectedExcludedTagValues = useMemo<readonly string[]>(
    () => tagMapToSelectedValues(config.tagsExcluded ?? EMPTY_TAG_MAP),
    [config.tagsExcluded],
  );
  // Two-way cross-feed (matches the manual flow): each select offers the full
  // catalog MINUS the leaves chosen in the other field, so a (group, value)
  // leaf can never be in both Included and Excluded at once.
  const includedOptionGroups = useMemo<readonly TagOptionGroup[]>(
    () => subtractOptionGroups(tagOptionGroups, selectedExcludedTagValues),
    [tagOptionGroups, selectedExcludedTagValues],
  );
  const excludedOptionGroups = useMemo<readonly TagOptionGroup[]>(
    () => subtractOptionGroups(tagOptionGroups, selectedTagValues),
    [tagOptionGroups, selectedTagValues],
  );
  // The Excluded field is offered only once at least one tag is Included
  // (mirrors the manual flow's conditional render).
  const hasIncludedTags = selectedTagValues.length > 0;

  const handleTagsChange = useCallback(
    (values: string[]) =>
      // Clearing all included tags unmounts the Excluded field; also clear
      // tagsExcluded so stale excluded leaves can't be silently persisted as
      // hidden exclusions the user can no longer see or edit.
      patchConfig({
        tagsIncluded: selectedValuesToTagMap(values),
        ...(values.length === 0 ? { tagsExcluded: EMPTY_TAG_MAP } : {}),
      }),
    [patchConfig],
  );
  const handleTagsExcludedChange = useCallback(
    (values: string[]) => patchConfig({ tagsExcluded: selectedValuesToTagMap(values) }),
    [patchConfig],
  );

  return (
    <Col gridGap="dimensions.spacing.400" width="100%">
      {/* Tags Included — required grouped multi-select (tag group → values,
       *  options from useTagsQuery). The grouped shape matches the manual
       *  question flow; selection round-trips through the draft's TagMap. */}
      <ConfigField
        label={t('configTagsIncludedLabel')}
        info={t('configTagsIncludedInfo')}
        infoAltText={infoAltText}
        isRequired
        {...(fieldErrors.tags ? { error: t('configTagsRequiredError') } : {})}
      >
        <GroupedOptionMultiSelect
          optionGroups={includedOptionGroups}
          values={selectedTagValues}
          onChange={handleTagsChange}
          placeholder={t('configTagsIncludedPlaceholder')}
          disabled={disabled}
          isRequired
          ariaLabel={t('configTagsIncludedLabel')}
          chipDismissAltText={t('configTagsIncludedRemoveAltText')}
          {...dtProps(dataTestId, 'tags')}
        />
      </ConfigField>

      {/* Tags Excluded — offered only once at least one tag is Included
       *  (matches the manual flow). Its option universe is the catalog minus
       *  the included leaves, so a leaf can't be in both. Optional (no
       *  validation), round-trips through the draft's tagsExcluded TagMap. */}
      {hasIncludedTags ? (
        <ConfigField
          label={t('configTagsExcludedLabel')}
          info={t('configTagsExcludedInfo')}
          infoAltText={infoAltText}
        >
          <GroupedOptionMultiSelect
            optionGroups={excludedOptionGroups}
            values={selectedExcludedTagValues}
            onChange={handleTagsExcludedChange}
            placeholder={t('configTagsExcludedPlaceholder')}
            disabled={disabled}
            ariaLabel={t('configTagsExcludedLabel')}
            chipDismissAltText={t('configTagsExcludedRemoveAltText')}
            {...dtProps(dataTestId, 'tags-excluded')}
          />
        </ConfigField>
      ) : null}
    </Col>
  );
}
